import type { ReactElement } from 'react';
import { Modal, type ModalAction } from './Modal';

/** A busy terminal (or other long-running tool) blocking close. */
export interface BusyTerminal {
  id: string;
  title: string;
  /** Optional secondary line, e.g. the working directory. */
  detail?: string;
}

export interface CloseConfirmDialogProps {
  open: boolean;
  /**
   * 'tab' → closing a single dirty tab (Save / Don't save / Cancel).
   * 'window' → closing the window with dirty docs (Save all & close / Don't save / Cancel).
   */
  variant: 'tab' | 'window';
  /** Name shown in the prompt (the tab's file/Untitled name; ignored for 'window'). */
  fileName?: string;
  /** Number of dirty docs (shown in the window variant's message). */
  dirtyCount?: number;
  /**
   * Terminals (or other tools) with a running process. When present and there
   * are no dirty docs the dialog renders the terminals-only variant; alongside
   * dirty docs it renders a second "running processes" section.
   */
  busyTerminals?: BusyTerminal[];
  /** Save (the tab) / Save all & close (the window), then close. */
  onSave: () => void;
  /** Discard unsaved changes and close. */
  onDontSave: () => void;
  /** Keep everything open (Esc / backdrop / Cancel). */
  onCancel: () => void;
  /**
   * Terminals-only variant: proceed with the close (sending SIGHUP to each
   * running process). Falls back to {@link onDontSave} when not supplied.
   */
  onCloseAnyway?: () => void;
}

/** Renders the list of busy terminals shared by the terminals-only and mixed variants. */
function BusyTerminalsSection({ terminals }: { terminals: BusyTerminal[] }): ReactElement {
  return (
    <div className="mp-close-terminals">
      <p className="mp-modal__text">
        Processes are still running in{' '}
        <strong>
          {terminals.length} terminal{terminals.length === 1 ? '' : 's'}
        </strong>
        . Closing will send SIGHUP to each.
      </p>
      <ul className="mp-close-terminals__list">
        {terminals.map((t) => (
          <li key={t.id} className="mp-close-terminals__item">
            <span className="mp-close-terminals__title">{t.title}</span>
            {t.detail && <span className="mp-close-terminals__detail">{t.detail}</span>}
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Unsaved-changes guard shown before closing a dirty tab or a window with dirty
 * docs (spec Task 55), now also covering terminals with a running process
 * (Slice 3). Built on the shared Modal (focus trap, Esc=Cancel). Save is the
 * primary action; Cancel is the safe default for Esc/backdrop.
 *
 * Three branches:
 *  - docs-only (no busy terminals): the original Save / Don't save / Cancel UI.
 *  - terminals-only (no dirty docs, ≥1 busy terminal): Cancel / Close anyway.
 *  - mixed (dirty docs + busy terminals): the docs prompt plus a terminals
 *    section; the chosen Save/Discard action sequences the terminal kill.
 */
export function CloseConfirmDialog({
  open,
  variant,
  fileName,
  dirtyCount = 1,
  busyTerminals,
  onSave,
  onDontSave,
  onCancel,
  onCloseAnyway,
}: CloseConfirmDialogProps): ReactElement {
  const terminals = busyTerminals ?? [];
  const hasDirtyDocs = dirtyCount > 0;
  const terminalsOnly = terminals.length > 0 && !hasDirtyDocs;

  // Terminals-only: no documents to save, just a running-process confirm.
  if (terminalsOnly) {
    // Destructive: "Close anyway" sends SIGHUP to running processes, so make
    // Cancel both the Esc target and the Enter default — Enter is always safe.
    const terminalActions: ModalAction[] = [
      { id: 'cancel', label: 'Cancel', onSelect: onCancel, accelerator: 'c', isCancel: true, isDefault: true },
      {
        id: 'close-anyway',
        label: 'Close anyway',
        onSelect: onCloseAnyway ?? onDontSave,
        accelerator: 'a',
        variant: 'danger',
      },
    ];
    return (
      <Modal open={open} onClose={onCancel} title="Close Slate?" actions={terminalActions}>
        <BusyTerminalsSection terminals={terminals} />
      </Modal>
    );
  }

  const isWindow = variant === 'window';
  const title = isWindow ? 'Unsaved changes' : 'Save changes?';
  const saveLabel = isWindow ? 'Save all & close' : 'Save';
  const docActions: ModalAction[] = [
    { id: 'cancel', label: 'Cancel', onSelect: onCancel, accelerator: 'c', isCancel: true },
    { id: 'dont-save', label: 'Don’t save', onSelect: onDontSave, accelerator: 'n' },
    { id: 'save', label: saveLabel, onSelect: onSave, accelerator: 's', variant: 'primary', isDefault: true },
  ];
  return (
    <Modal open={open} onClose={onCancel} title={title} actions={docActions}>
      {isWindow ? (
        <p className="mp-modal__text">
          You have unsaved changes in{' '}
          <strong>
            {dirtyCount} document{dirtyCount === 1 ? '' : 's'}
          </strong>
          . Save them before closing?
        </p>
      ) : (
        <p className="mp-modal__text">
          <strong>{fileName ?? 'This document'}</strong> has unsaved changes. Save them
          before closing?
        </p>
      )}
      {terminals.length > 0 && <BusyTerminalsSection terminals={terminals} />}
    </Modal>
  );
}
